import { shedStore } from '../shedStore';

/**
 * Reads the dimensions slice of the shed state.
 *
 * @returns {IDimensionsState} The current dimensions state.
 */
const dimensions = () => shedStore.getState().dimensions;

/**
 * Gets the shed width.
 *
 * @returns {number} The shed width, or NaN until it is set.
 */
export const getWidth = () => parseFloat(dimensions().width);

/**
 * Sets the shed width.
 *
 * @param {number} width - The shed width.
 */
export const setWidth = (width: number) => shedStore.setState({ dimensions: { width } });

/**
 * Gets the shed depth.
 *
 * @returns {number} The shed depth, or NaN until it is set.
 */
export const getDepth = () => parseFloat(dimensions().depth);

/**
 * Sets the shed depth.
 *
 * @param {number} depth - The shed depth.
 */
export const setDepth = (depth: number) => shedStore.setState({ dimensions: { depth } });

/**
 * Gets the door frame height adjustment.
 *
 * Applies to height only: `getWallHeight()` includes it, `getRawWallHeight()` does not.
 *
 * @returns {number} The door frame height adjustment, or 0 when unset.
 */
export const getDoorFrameHeightAdjust = () => dimensions().doorFrameHeightAdjust;

/**
 * Sets the door frame height adjustment, falling back to 0 for negative or non-numeric
 * values. Mirrors the `min => 0` rule on the ACF field.
 *
 * @param {number} value - The adjustment
 */
export const setDoorFrameHeightAdjust = (value: number) => {
	const doorFrameHeightAdjust = Number(value);

	shedStore.setState({
		dimensions: {
			doorFrameHeightAdjust:
				!isNaN(doorFrameHeightAdjust) && doorFrameHeightAdjust > 0
					? doorFrameHeightAdjust
					: 0,
		},
	});
};

/**
 * Gets the wall height WITHOUT the door frame adjustment. Use this for comparisons against
 * configured sizes — option matching, price conditions, size labels.
 *
 * @returns {number} The stored wall height.
 */
export const getRawWallHeight = () => parseFloat(dimensions().wallHeight);

/**
 * Gets the wall height INCLUDING the door frame adjustment.
 *
 * @returns {number} The adjusted wall height.
 */
export const getWallHeight = () =>
	parseFloat(dimensions().wallHeight) + dimensions().doorFrameHeightAdjust;

/**
 * Sets the wall height.
 *
 * @param {number} height - The wall height.
 */
export const setWallHeight = (height: number) =>
	shedStore.setState({ dimensions: { wallHeight: height } });

/**
 * Gets the shed height.
 *
 * @returns {number} The building height.
 */
export const getBuildingHeight = () => Number(dimensions().buildingHeight);

/**
 * Sets the shed height.
 *
 * @param {number} height - The building height.
 */
export const setBuildingHeight = (height: number) =>
	shedStore.setState({ dimensions: { buildingHeight: height } });
